import {
  ChangeDetectorRef,
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';

import { ColorPickerComponent } from './color-picker.component';
import { ColorPickerService } from './color-picker.service';

type ColorPickerChange = { slider: string; value: { v: number; rg: number } };

@Directive({
  selector: '[rtColorPicker]',
  host: {
    '(input)': 'changeInput($event.target.value)',
    '(click)': 'onClick()',
  },
})
export class ColorPickerDirective implements OnInit, OnChanges {
  @Input('rtColorPicker') public colorPicker: string;
  @Input('cpToggle') public cpToggle: boolean;
  @Input('cpPosition') public cpPosition: string = 'right';
  @Input('cpPositionOffset') public cpPositionOffset: string = '0%';
  @Input('cpPositionRelativeToArrow') public cpPositionRelativeToArrow: boolean = false;
  @Input('cpOutputFormat') public cpOutputFormat: string = 'hex';
  @Input('cpPresetLabel') public cpPresetLabel: string = 'Preset colors';
  @Input('cpPresetColors') public cpPresetColors: string[];
  @Input('cpCancelButton') public cpCancelButton: boolean = false;
  @Input('cpCancelButtonClass') public cpCancelButtonClass: string = 'cp-cancel-button-class';
  @Input('cpCancelButtonText') public cpCancelButtonText: string = 'Cancel';
  @Input('cpOKButton') public cpOKButton: boolean = false;
  @Input('cpOKButtonClass') public cpOKButtonClass: string = 'cp-ok-button-class';
  @Input('cpOKButtonText') public cpOKButtonText: string = 'OK';
  @Input('cpFallbackColor') public cpFallbackColor: string = '#ffffff';
  @Input('cpHeight') public cpHeight: string = 'auto';
  @Input('cpWidth') public cpWidth: string = '230px';
  @Input('cpIgnoredElements') public cpIgnoredElements: any[] = [];
  @Input('cpDialogDisplay') public cpDialogDisplay: string = 'popup';
  @Input('cpSaveClickOutside') public cpSaveClickOutside: boolean = true;
  @Input('cpAlphaChannel') public cpAlphaChannel: string = 'hex6';

  @Output()
  public colorPickerSelect: EventEmitter<string> = new EventEmitter(true);
  @Output()
  public colorPickerChange: EventEmitter<string> = new EventEmitter(false);
  @Output() public cpInputChange: EventEmitter<ColorPickerChange> = new EventEmitter(true);
  @Output() public cpSliderChange: EventEmitter<ColorPickerChange> = new EventEmitter(true);
  @Output()
  public cpToggleChange: EventEmitter<boolean> = new EventEmitter(true);

  private dialog: ColorPickerComponent;
  private created: boolean;
  private ignoreChanges: boolean = false;

  constructor(
    private vcRef: ViewContainerRef,
    private el: ElementRef,
    private service: ColorPickerService,
    private cfr: ComponentFactoryResolver,
    private cdRef: ChangeDetectorRef,
  ) {
    this.created = false;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.cpToggle) {
      if (changes.cpToggle.currentValue) this.openDialog();
      if (!changes.cpToggle.currentValue && this.dialog) this.dialog.closeColorPicker();
    }
    if (changes.colorPicker) {
      if (this.dialog && !this.ignoreChanges) {
        if (this.cpDialogDisplay === 'inline') {
          this.dialog.setInitialColor(changes.colorPicker.currentValue);
        }
        this.dialog.setColorFromString(changes.colorPicker.currentValue, false);
      }
      this.ignoreChanges = false;
    }
    if (changes.cpPresetLabel || changes.cpPresetColors) {
      if (this.dialog) {
        this.dialog.setPresetConfig(this.cpPresetLabel, this.cpPresetColors);
      }
    }
  }

  public ngOnInit(): void {
    let hsva = this.service.stringToHsva(this.colorPicker);
    if (hsva === null) hsva = this.service.stringToHsva(this.colorPicker, true);
    if (hsva == null) {
      hsva = this.service.stringToHsva(this.cpFallbackColor);
    }
    const color = this.service.outputFormat(
      hsva,
      this.cpOutputFormat,
      this.cpAlphaChannel === 'hex8',
    );
    if (color !== this.colorPicker) {
      // setTimeout(() => {
      this.colorPickerChange.emit(color);
      this.cdRef.detectChanges();
      // }, 0);
    }
  }

  public onClick(): void {
    if (this.cpIgnoredElements.filter(item => item === this.el.nativeElement).length === 0) {
      this.openDialog();
      this.cdRef.detectChanges();
    }
  }

  public colorChanged(value: string, ignore: boolean = true): void {
    this.ignoreChanges = ignore;
    this.colorPickerChange.emit(value);
  }

  public colorSelected(value: string): void {
    this.colorPickerSelect.emit(value);
  }

  public inputChanged(event: ColorPickerChange): void {
    this.cpInputChange.emit(event);
  }

  public sliderChanged(event: ColorPickerChange): void {
    this.cpSliderChange.emit(event);
  }

  public changeInput(value: string): void {
    this.dialog.setColorFromString(value, true);
  }

  public toggle(value: boolean): void {
    this.cpToggleChange.emit(value);
  }

  private openDialog(): void {
    if (!this.created) {
      this.created = true;
      const compFactory = this.cfr.resolveComponentFactory(ColorPickerComponent);
      // TODO ReflectiveInjector is deprecated as of angular v5,
      // we should use Injector instead
      const injector = Injector.create({ providers: [], parent: this.vcRef.parentInjector });

      const cmpRef = this.vcRef.createComponent(compFactory, 0, injector, []);
      cmpRef.instance.setDialog(
        this,
        this.el,
        this.colorPicker,
        this.cpPosition,
        this.cpPositionOffset,
        this.cpOutputFormat,
        this.cpPresetLabel,
        this.cpPresetColors,
        this.cpCancelButton,
        this.cpCancelButtonClass,
        this.cpCancelButtonText,
        this.cpOKButton,
        this.cpOKButtonClass,
        this.cpOKButtonText,
        this.cpHeight,
        this.cpWidth,
        this.cpIgnoredElements,
        this.cpDialogDisplay,
        this.cpSaveClickOutside,
        this.cpAlphaChannel,
      );
      this.dialog = cmpRef.instance;
    } else if (this.dialog) {
      this.dialog.openDialog(this.colorPicker);
    }
  }
}
